import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "~/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium ring-offset-background transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-primary-700 text-contrast hover:opacity-90",
        destructive: "bg-destructive text-primary-foreground hover:opacity-90",
        outline:
          "border border-input bg-background text-primary-800 hover:text-accent-foreground hover:border-primary-500 hover:opacity-90",
        "brand-outline":
          "border border-landing-border bg-white text-landing-text hover:border-landing-red hover:text-landing-red",
        primary: "bg-primary-700 text-contrast hover:opacity-90",
        brand:
          "bg-landing-red text-white hover:bg-landing-red-hover shadow-landing-red hover:-translate-y-0.5 hover:shadow-landing-red-hover",
        secondary: "bg-secondary-600 text-contrast hover:opacity-90",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-lg px-3",
        lg: "h-11 rounded-lg px-8",
        icon: "size-10",
        xs: "h-6 rounded-md p-1 text-xs",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        data-cuelume-press={props.disabled ? undefined : ""}
        data-cuelume-release={props.disabled ? undefined : ""}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
